import { Eye, FileText, Printer, ShieldCheck, X } from 'lucide-react';
import CommercialOfferBox from './CommercialOfferBox';
import { useEffect, useMemo, useState } from 'react';
import { tenantService } from '../services/api';

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000,
  padding: '24px'
};

const modalStyle = {
  background: 'white',
  borderRadius: '16px',
  width: 'min(1100px, 100%)',
  maxHeight: '90vh',
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
};

function formatAcceptedAt(value) {
  if (!value) return 'Data non disponibile';
  return new Date(value).toLocaleString('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function buildPrintableDocumentHtml(documentItem, tenant) {
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8" />
        <title>${documentItem.title || 'Documento legale SoonTip'}</title>
        <style>
          :root { color-scheme: light; }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: Arial, sans-serif;
            color: #0f172a;
            line-height: 1.6;
            background: #ffffff;
          }
          .document-shell {
            max-width: 920px;
            margin: 0 auto;
            padding: 24px 28px;
          }
          .document-meta {
            margin-bottom: 20px;
            padding-bottom: 16px;
            border-bottom: 1px solid #e2e8f0;
          }
          .document-meta h1 {
            margin: 0 0 6px;
            font-size: 28px;
          }
          .document-meta p {
            margin: 0;
            color: #475569;
          }
          .document-shell h1,
          .document-shell h2,
          .document-shell h3 {
            color: #111827;
          }
          .document-shell p {
            margin: 0 0 12px;
          }
          .document-shell ul,
          .document-shell ol {
            margin: 0 0 14px 22px;
            padding-left: 14px;
          }
          .document-shell li {
            margin: 4px 0;
          }
          .document-shell table {
            width: 100%;
            border-collapse: collapse;
            margin: 14px 0;
          }
          .document-shell th,
          .document-shell td {
            border: 1px solid #d1d5db;
            padding: 6px 8px;
            text-align: left;
            vertical-align: top;
          }
          .document-shell pre {
            white-space: pre-wrap;
            word-break: break-word;
          }
          .document-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #cbd5e1;
            color: #475569;
            font-size: 13px;
          }
          .document-footer p {
            margin: 0 0 6px;
          }
          @page { margin: 2cm; }
          @media print {
            body { margin: 0; }
            .document-shell { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="document-shell">
          <div class="document-meta">
            <h1>${documentItem.title || 'Documento legale SoonTip'}</h1>
            <p>Versione ${documentItem.version || 'N/D'} · ${formatAcceptedAt(documentItem.acceptedAt)}</p>
          </div>

          ${documentItem.html || '<p>Contenuto non disponibile.</p>'}

          <div class="document-footer">
            <p><strong>Tenant:</strong> ${tenant.companyName || 'N/D'}</p>
            <p><strong>Firmatario:</strong> ${[documentItem?.signer?.fullName, documentItem?.signer?.email].filter(Boolean).join(' — ') || 'N/D'}</p>
            <p><strong>Origine contenuto:</strong> ${documentItem.source === 'SNAPSHOT' ? 'Snapshot salvato al momento dell\'accettazione' : documentItem.source === 'FALLBACK' ? 'Fallback documento associato (no snapshot)' : 'Contenuto non disponibile'}</p>
          </div>
        </div>
      </body>
    </html>
  `;
}

function printLegalDocument(documentItem, tenant) {
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    throw new Error('Impossibile aprire la finestra di stampa. Controlla le impostazioni del browser.');
  }

  printWindow.document.write(buildPrintableDocumentHtml(documentItem, tenant));
  printWindow.document.close();

  window.setTimeout(() => {
    printWindow.focus?.();
    printWindow.print();
  }, 350);
}

const sourceBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 8px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 600
};

const sectionCardStyle = {
  border: '1px solid #e2e8f0',
  borderRadius: '14px',
  padding: '18px 20px',
  background: '#ffffff'
};

function prettyFeatureValue(feature) {
  if (!feature) return '—';
  if (feature.displayValue !== undefined && feature.displayValue !== null) return String(feature.displayValue);

  const valueType = feature.valueType;
  const value = feature.value;
  if (valueType === 'BOOLEAN') return value ? 'Sì' : 'No';
  if ((valueType === 'INTEGER' || valueType === 'FLOAT') && (value === null || value === undefined || Number(value) === 0)) return 'Illimitato';
  if (valueType === 'JSON') {
    if (value && typeof value === 'object' && value.feePercent !== undefined) {
      const fixed = Number(value.feeFixedCents || 0) / 100;
      return `${value.feePercent}% + EUR ${fixed.toFixed(2)}`;
    }
    return JSON.stringify(value || {});
  }
  return String(value ?? '');
}

function renderTypeLabel(documentItem) {
  if (documentItem.category === 'CONTRACT') return 'Contratto legacy';
  if (documentItem.category === 'CONSENT') return 'Consenso';
  return documentItem.category || 'Documento';
}

function DocumentCard({
  documentItem,
  expandedIds,
  onToggleExpanded,
  onPreview,
  onPrint,
  printingId,
  showOfferBox = false,
}) {
  const canRender = Boolean(documentItem.html);
  const isSnapshot = documentItem.source === 'SNAPSHOT';
  const isExpanded = expandedIds.has(documentItem.id);
  const hasExtras = documentItem.consentMethod || documentItem.otpMethod || documentItem.otpVerifiedAt || documentItem.sha256 || documentItem.ip || documentItem.userAgent || documentItem.legalDocumentId;

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '14px',
        padding: '18px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        gap: '16px',
        alignItems: 'flex-start',
        background: '#ffffff'
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <FileText size={18} color="#334155" />
          <strong style={{ color: '#0f172a' }}>{documentItem.title}</strong>
          <span style={{ color: '#64748b', fontSize: '14px' }}>(v{documentItem.version || 'N/D'})</span>
          <span
            style={{
              ...sourceBadgeStyle,
              background: isSnapshot ? '#dcfce7' : '#fef3c7',
              color: isSnapshot ? '#166534' : '#92400e'
            }}
          >
            {isSnapshot ? 'Snapshot audit' : documentItem.source === 'FALLBACK' ? 'Fallback documento associato (no snapshot)' : 'HTML non disponibile'}
          </span>
        </div>

        <div style={{ marginTop: '10px', color: '#475569', fontSize: '14px', display: 'grid', gap: '4px' }}>
          <div><strong>Tipo:</strong> {renderTypeLabel(documentItem)}</div>
          <div><strong>Accettato il:</strong> {formatAcceptedAt(documentItem.acceptedAt)}</div>
          {(documentItem.signer?.email || documentItem.signer?.fullName) && (
            <div>
              <strong>Firmatario:</strong>{' '}
              {[documentItem.signer.fullName, documentItem.signer.email].filter(Boolean).join(' — ')}
            </div>
          )}

          {showOfferBox && (
            <CommercialOfferBox
              label={documentItem.commercialOfferLabel}
              feePercent={documentItem.offerFeePercent}
              feeFixedCents={documentItem.offerFeeFixedCents}
              trialDays={documentItem.offerTrialDays}
              setupFeeCents={documentItem.offerSetupFeeCents}
              softwareSubscriptionFeeCents={documentItem.offerSoftwareSubscriptionFeeCents}
              notes={documentItem.commercialOfferNotes}
              style={{ marginTop: '10px' }}
            />
          )}

          {hasExtras && (
            <div style={{ marginTop: '8px' }}>
              <button
                onClick={() => onToggleExpanded(documentItem.id)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  cursor: 'pointer', color: '#3b82f6', fontSize: '13px',
                  display: 'flex', alignItems: 'center', gap: '4px'
                }}
              >
                {isExpanded ? '▲' : '▼'} {isExpanded ? 'Nascondi dettagli' : 'Ulteriori informazioni'}
              </button>

              {isExpanded && (
                <div style={{ marginTop: '8px', display: 'grid', gap: '4px', paddingLeft: '8px', borderLeft: '2px solid #e2e8f0' }}>
                  {documentItem.consentMethod && <div><strong>Metodo consenso:</strong> {documentItem.consentMethod}</div>}
                  {documentItem.otpMethod && <div><strong>Metodo OTP:</strong> {documentItem.otpMethod}</div>}
                  {documentItem.otpVerifiedAt && <div><strong>OTP verificato il:</strong> {formatAcceptedAt(documentItem.otpVerifiedAt)}</div>}
                  {documentItem.sha256 && (
                    <div style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                      <strong>SHA-256:</strong> {documentItem.sha256}
                    </div>
                  )}
                  {documentItem.ip && <div><strong>IP:</strong> {documentItem.ip}</div>}
                  {documentItem.userAgent && (
                    <div style={{ fontSize: '12px', color: '#94a3b8', wordBreak: 'break-all' }}>
                      <strong style={{ color: '#475569' }}>User-Agent:</strong> {documentItem.userAgent}
                    </div>
                  )}
                  {documentItem.legalDocumentId && (
                    <div style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>
                      <strong style={{ color: '#475569' }}>Doc ID:</strong> {documentItem.legalDocumentId}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <button
          onClick={() => onPreview(documentItem)}
          disabled={!canRender}
          className="btn"
          style={{ margin: 0, padding: '10px 16px', opacity: canRender ? 1 : 0.5, cursor: canRender ? 'pointer' : 'not-allowed' }}
        >
          <Eye size={16} /> Vedi
        </button>
        <button
          onClick={() => onPrint(documentItem)}
          disabled={!canRender || printingId === documentItem.id}
          className="btn"
          style={{
            margin: 0,
            padding: '10px 16px',
            background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)',
            opacity: canRender ? 1 : 0.5,
            cursor: canRender ? 'pointer' : 'not-allowed'
          }}
        >
          <Printer size={16} /> {printingId === documentItem.id ? 'Apertura stampa...' : 'Stampa / Salva PDF'}
        </button>
      </div>
    </div>
  );
}

const TenantLegalDocumentsModal = ({ open, tenantId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [printingId, setPrintingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!open || !tenantId) return;

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        setPreviewDocument(null);
        const response = await tenantService.getTenantLegalDocuments(tenantId);
        if (!cancelled) {
          setData(response);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load tenant legal documents:', err);
          setError(err.message || 'Errore durante il caricamento dei documenti legali');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, tenantId]);

  const documents = useMemo(() => data?.legalDocuments || [], [data]);
  const planDocuments = data?.planDocuments || {};
  const activeOffer = planDocuments?.activeOffer || null;
  const activePlan = planDocuments?.activePlan || null;
  const activePlanOverride = planDocuments?.activePlanOverride || null;
  const planContractDocument = planDocuments?.contract || null;
  const latestAnnexDocument = planDocuments?.latestAnnex || null;
  const displayedPlan = activePlanOverride || activePlan;
  const activePlanFeatures = useMemo(() => {
    const overrideFeatures = activePlanOverride?.features || displayedPlan?.features || [];
    const baseByKey = new Map((activePlan?.features || []).map((feature) => [feature.key, feature]));

    return overrideFeatures.map((feature) => {
      const baseFeature = baseByKey.get(feature.key) || null;
      const overrideDisplay = prettyFeatureValue(feature);
      const baseDisplay = baseFeature ? prettyFeatureValue(baseFeature) : null;
      const isOverridden = Boolean(activePlanOverride) && (!baseFeature || baseDisplay !== overrideDisplay);

      return {
        ...feature,
        overrideDisplay,
        baseDisplay,
        isOverridden,
      };
    });
  }, [activePlan, activePlanOverride, displayedPlan]);
  const hasFeatureOverrides = activePlanFeatures.some((feature) => feature.isOverridden);
  const hasFeeOverride = Boolean(
    activePlanOverride
    && activePlanOverride.baseFeePercent != null
    && (
      activePlanOverride.feePercent !== activePlanOverride.baseFeePercent
      || (activePlanOverride.feeFixedCents ?? 0) !== (activePlanOverride.baseFeeFixedCents ?? 0)
    )
  );
  const hasEffectiveOverrides = hasFeatureOverrides || hasFeeOverride;
  const appliedPlanLabel = hasEffectiveOverrides
    ? 'Piano applicato (con condizioni personalizzate)'
    : activeOffer
      ? 'Piano applicato tramite offerta commerciale'
      : 'Piano attivo';

  const legacyContractDocuments = useMemo(
    () => documents.filter((documentItem) => documentItem.category === 'CONTRACT'),
    [documents]
  );

  const consentDocuments = useMemo(
    () => documents.filter((documentItem) => documentItem.category === 'CONSENT'),
    [documents]
  );

  const hasFallbackDocuments = useMemo(
    () => documents.some((documentItem) => documentItem.source === 'FALLBACK'),
    [documents]
  );

  if (!open) return null;

  const tenant = data?.tenant || {};

  const handlePrint = (documentItem) => {
    if (!documentItem?.html) return;

    try {
      setPrintingId(documentItem.id);
      printLegalDocument(documentItem, tenant);
    } catch (err) {
      console.error('Error printing legal document:', err);
      alert(err.message || 'Errore durante l\'apertura della stampa. Riprova.');
    } finally {
      window.setTimeout(() => setPrintingId(null), 600);
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(event) => event.stopPropagation()}>
        <div style={{ padding: '24px 28px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', color: '#0f172a' }}>
              <ShieldCheck size={22} />
              Legal documents
            </h3>
            <p style={{ margin: '8px 0 0', color: '#475569' }}>
              {tenant.companyName || 'Tenant'}: storico legale completo (legacy + piano attivo).
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: '4px' }}
            title="Chiudi"
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ padding: '24px 28px', overflowY: 'auto' }}>
          {loading && <div className="loading">Caricamento documenti legali...</div>}
          {error && <div className="error">{error}</div>}

          {!loading && !error && documents.length === 0 && !planContractDocument && !latestAnnexDocument && !displayedPlan && !activeOffer && (
            <div style={{ padding: '24px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#475569' }}>
              Nessun documento legale disponibile per questo tenant.
            </div>
          )}

          {!loading && !error && (
            <div style={{ display: 'grid', gap: '14px' }}>
              {activeOffer && (
                <div style={{ ...sectionCardStyle, borderColor: '#fbcfe8', background: 'linear-gradient(180deg, #fff1f2 0%, #ffffff 45%)' }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#be185d', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Offerta commerciale attiva</div>
                  </div>
                  <p style={{ margin: 0, color: '#9d174d', fontSize: 14 }}>
                    Nome offerta: <strong>{activeOffer.label || 'N/D'}</strong>
                  </p>
                </div>
              )}

              {displayedPlan && (
                <div style={{ ...sectionCardStyle, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 40%)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: 12, color: activePlanOverride ? '#be185d' : '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                        {appliedPlanLabel}
                      </div>
                      <h4 style={{ margin: '6px 0 4px', color: '#0f172a' }}>
                        {displayedPlan.name || '—'} {displayedPlan.version != null ? <span style={{ color: '#64748b', fontWeight: 500 }}>(v{displayedPlan.version})</span> : null}
                      </h4>
                      <p style={{ margin: 0, color: '#475569', fontSize: 13 }}>
                        {displayedPlan.code ? `Codice: ${displayedPlan.code} · ` : ''}
                        Origine: {displayedPlan.source === 'active-subscription' ? 'Subscription attiva' : displayedPlan.source === 'latest-annex' ? 'Ultimo Allegato A' : 'Contratto piano'}
                      </p>
                    </div>
                    {displayedPlan.status && (
                      <span style={{ ...sourceBadgeStyle, background: activePlanOverride ? '#fce7f3' : '#dbeafe', color: activePlanOverride ? '#be185d' : '#1d4ed8' }}>{displayedPlan.status}</span>
                    )}
                  </div>

                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 8 }}>
                      Feature incluse
                    </div>
                    {activePlanFeatures.length === 0 ? (
                      <div style={{ color: '#64748b', fontSize: 13 }}>Nessuna feature disponibile nel piano attivo.</div>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        {displayedPlan.feePercent != null && (() => {
                          const fmtFee = (pct, fixed) => `${pct}% + \u20ac${((fixed ?? 0) / 100).toFixed(2)}`;
                          const effectivePct   = displayedPlan.feePercent;
                          const effectiveFixed = displayedPlan.feeFixedCents ?? 0;
                          const basePct   = displayedPlan.baseFeePercent;
                          const baseFixed = displayedPlan.baseFeeFixedCents ?? 0;
                          const isFeeOverridden = basePct != null && (effectivePct !== basePct || effectiveFixed !== baseFixed);
                          return (
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: '1px dashed #e2e8f0', paddingBottom: 6 }}>
                              <span style={{ color: '#0f172a', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                Commissione SoonTip
                                {isFeeOverridden && <span style={{ ...sourceBadgeStyle, background: '#fce7f3', color: '#be185d', fontSize: 10, padding: '2px 6px' }}>Override</span>}
                              </span>
                              {isFeeOverridden ? (
                                <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, textAlign: 'right' }}>
                                  <span style={{ color: '#be185d', fontSize: 13, fontWeight: 700 }}>{fmtFee(effectivePct, effectiveFixed)}</span>
                                  <span style={{ color: '#94a3b8', fontSize: 12, textDecoration: 'line-through' }}>{fmtFee(basePct, baseFixed)}</span>
                                </span>
                              ) : (
                                <span style={{ color: '#334155', fontSize: 13, textAlign: 'right' }}>{fmtFee(effectivePct, effectiveFixed)}</span>
                              )}
                            </div>
                          );
                        })()}
                        {activePlanFeatures.map((feature) => (
                          <div key={feature.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderBottom: '1px dashed #e2e8f0', paddingBottom: 6 }}>
                            <span style={{ color: '#0f172a', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              {feature.name || feature.key}
                              {feature.isOverridden && (
                                <span style={{ ...sourceBadgeStyle, background: '#fce7f3', color: '#be185d', fontSize: 10, padding: '2px 6px' }}>
                                  Override
                                </span>
                              )}
                            </span>
                            {feature.isOverridden ? (
                              <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, textAlign: 'right' }}>
                                <span style={{ color: '#be185d', fontSize: 13, fontWeight: 700 }}>{feature.overrideDisplay}</span>
                                {feature.baseDisplay && (
                                  <span style={{ color: '#94a3b8', fontSize: 12, textDecoration: 'line-through' }}>{feature.baseDisplay}</span>
                                )}
                              </span>
                            ) : (
                              <span style={{ color: '#334155', fontSize: 13, textAlign: 'right' }}>{feature.overrideDisplay}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {planContractDocument && (
                <div style={{ ...sectionCardStyle, borderColor: '#bfdbfe' }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#1e40af', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Contratto piano</div>
                  </div>
                  <DocumentCard
                    documentItem={planContractDocument}
                    expandedIds={expandedIds}
                    onToggleExpanded={toggleExpanded}
                    onPreview={setPreviewDocument}
                    onPrint={handlePrint}
                    printingId={printingId}
                  />
                </div>
              )}

              {latestAnnexDocument && (
                <div style={{ ...sectionCardStyle, borderColor: '#c7d2fe' }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#3730a3', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Allegato A</div>
                    <p style={{ margin: '4px 0 0', color: '#4338ca', fontSize: 13 }}>Mostrata solo l'ultima versione disponibile.</p>
                  </div>
                  <DocumentCard
                    documentItem={latestAnnexDocument}
                    expandedIds={expandedIds}
                    onToggleExpanded={toggleExpanded}
                    onPreview={setPreviewDocument}
                    onPrint={handlePrint}
                    printingId={printingId}
                  />
                </div>
              )}

              {legacyContractDocuments.length > 0 && (
                <div style={{ ...sectionCardStyle, borderColor: '#fcd34d' }}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Contratti legacy</div>
                    <p style={{ margin: '4px 0 0', color: '#78350f', fontSize: 13 }}>Contratti firmati nel flusso pre-piano con offerta commerciale.</p>
                  </div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {legacyContractDocuments.map((documentItem) => (
                      <DocumentCard
                        key={documentItem.id}
                        documentItem={documentItem}
                        expandedIds={expandedIds}
                        onToggleExpanded={toggleExpanded}
                        onPreview={setPreviewDocument}
                        onPrint={handlePrint}
                        printingId={printingId}
                        showOfferBox
                      />
                    ))}
                  </div>
                </div>
              )}

              {consentDocuments.length > 0 && (
                <div style={sectionCardStyle}>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Consensi privacy e termini</div>
                  </div>
                  <div style={{ display: 'grid', gap: 12 }}>
                    {consentDocuments.map((documentItem) => (
                      <DocumentCard
                        key={documentItem.id}
                        documentItem={documentItem}
                        expandedIds={expandedIds}
                        onToggleExpanded={toggleExpanded}
                        onPreview={setPreviewDocument}
                        onPrint={handlePrint}
                        printingId={printingId}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!displayedPlan && !activeOffer && !planContractDocument && !latestAnnexDocument && legacyContractDocuments.length === 0 && consentDocuments.length === 0 && (
                <div style={{ padding: '24px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', color: '#475569' }}>
                  Nessun documento legale disponibile per questo tenant.
                </div>
              )}

              {hasFallbackDocuments && (
                <div
                  style={{
                    marginTop: '4px',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    border: '1px solid #fde68a',
                    background: '#fffbeb',
                    color: '#92400e',
                    fontSize: '13px'
                  }}
                >
                  Nota: "Fallback documento associato (no snapshot)" indica che per quella accettazione non era presente uno snapshot HTML salvato.
                  Per i contratti viene mostrato il documento legale collegato al contractId associato all'accettazione;
                  per i consensi viene mostrato il documento della stessa tipologia/versione disponibile a sistema.
                </div>
              )}
            </div>
          )}
        </div>

        {previewDocument && (
          <div style={overlayStyle} onClick={() => setPreviewDocument(null)}>
            <div
              style={{
                ...modalStyle,
                width: 'min(1200px, 100%)',
                maxHeight: '92vh'
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center' }}>
                <div>
                  <h4 style={{ margin: 0, color: '#0f172a' }}>{previewDocument.title}</h4>
                  <p style={{ margin: '6px 0 0', color: '#64748b' }}>
                    Versione {previewDocument.version} · {formatAcceptedAt(previewDocument.acceptedAt)}
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={() => handlePrint(previewDocument)}
                    className="btn"
                    style={{
                      margin: 0,
                      padding: '10px 16px',
                      background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)'
                    }}
                  >
                    <Printer size={16} /> {printingId === previewDocument.id ? 'Apertura stampa...' : 'Stampa / Salva PDF'}
                  </button>
                  <button
                    onClick={() => setPreviewDocument(null)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#64748b', padding: '4px' }}
                    title="Chiudi anteprima"
                  >
                    <X size={22} />
                  </button>
                </div>
              </div>

              <div style={{ overflowY: 'auto', padding: '24px', background: '#f8fafc' }}>
                <div style={{ maxWidth: '900px', margin: '0 auto', background: '#fff', padding: '32px', borderRadius: '12px', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
                  <div dangerouslySetInnerHTML={{ __html: previewDocument.html }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantLegalDocumentsModal;